import { FC, useEffect, useState } from 'react';
import _ from 'lodash';
import KeywordForm from '../KeywordForm';
import DisplayForm from '../DisplayForm';
import ShopForm from '../ShopForm';
import RecommendForm from '../RecommendForm';
import { AdType, RtbCampaign } from 'core/rtbCampaign/RtbCampaign';
import { useCampaignStore } from 'store/campaignStore';
import { useProductStore } from 'store/productStore';
import useCreativeStore from 'store/creativeStore';
import { Redirect, useParams } from 'react-router-dom';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager,
} from 'core/rtbCampaign/RtbCampaignManager';
import {
  CreativeManager,
  DefaultCreativeManager,
} from 'core/creative/CreativeManager';
import { SelectPattern } from 'core/rtbCampaign/singlePage/common';
import { CampaignBannerMapState } from 'core/creative/Creative';
import { Product } from 'core/product/Product';
import { LoadingIndicator } from 'components/LoadingIndicator';
import moment from 'moment';
import { useOrderStore } from 'store/orderStore';
import { ROUTE_PATH } from 'enum/RoutePath';
import OutboundKeywordForm from '../OutboundKeywordForm';

const defaultCampaignManager: RtbCampaignManager =
  new DefaultRtbCampaignManager();
const creativeManager: CreativeManager = new DefaultCreativeManager();

const CopyPage: FC = () => {
  const { order } = useOrderStore();
  const { handleSetCampaign } = useCampaignStore();
  const { resetProductStore } = useProductStore();
  const { handleAddCreatives } = useCreativeStore();
  const [defaultCampaign, setCampaign] = useState<RtbCampaign>(
    {} as RtbCampaign,
  );
  const params = useParams();

  const formType = () => {
    switch (defaultCampaign?.basic?.adType) {
      case AdType.KEYWORD:
        return <KeywordForm defaultForm={defaultCampaign} />;
      case AdType.SMART_KEYWORD:
        return <KeywordForm defaultForm={defaultCampaign} />;
      case AdType.MAX_CVR_KEYWORD:
        return <KeywordForm defaultForm={defaultCampaign} />;
      case AdType.SHOP:
        return <ShopForm defaultForm={defaultCampaign} />;
      case AdType.DISPLAY:
        return <DisplayForm defaultForm={defaultCampaign} />;
      case AdType.RECOMMEND:
        if (defaultCampaign.basic?.selectPattern !== SelectPattern.AUTO) {
          return (
            <Redirect
              push
              to={{
                pathname: `/orders/${order.orderNumber}/${ROUTE_PATH.ERROR403}`,
                state: { message: 'canViewAllAccounts test failed' },
              }}
            />
          );
        } else {
          return <RecommendForm defaultForm={defaultCampaign} />;
        }
      case AdType.OUTBOUND_KEYWORD:
        return <OutboundKeywordForm defaultForm={defaultCampaign} />;
      default:
        return '';
    }
  };

  const updateCampaignProducts = (creatives: any, key: string) => {
    return _.compact(
      creatives
        .filter(
          (creative) =>
            creative.bindingState !== CampaignBannerMapState.DISABLE,
        )
        .map((creative) => creative[key]),
    );
  };

  useEffect(() => {
    const fetchCreative = async (campaignId: string, camPaign: RtbCampaign) => {
      try {
        const startDate = moment(camPaign.basic.startDate);
        const endDate = moment(camPaign.basic.endDate);

        const copyStartDate = () => {
          // 如果活動開始時間是今天或過去時間，則開始時間為今天+1小時
          if (startDate.isSameOrBefore(moment())) {
            return moment()
              .add(1, 'hour')
              .startOf('hour')
              .format('YYYY-MM-DD HH:mm:ss');
          }
          // 如果活動開始時間是未來，則直接複製開始時間
          return startDate.format('YYYY-MM-DD HH:mm:ss');
        };

        const copyEndDate = () => {
          // 如果活動開始時間是今天或過去時間，則結束時間為今天+舊活動區間並且不能超過訂單結束時間
          if (startDate.isSameOrBefore(moment())) {
            return moment()
              .add(endDate.diff(startDate, 'days'), 'days')
              .isAfter(moment(order.endDate).format('YYYY-MM-DD HH:mm:ss'))
              ? moment(order.endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')
              : moment()
                  .add(endDate.diff(startDate, 'days'), 'days')
                  .endOf('day')
                  .format('YYYY-MM-DD HH:mm:ss');
          }
          // 如果活動開始時間是未來，則直接複製結束時間
          return endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss');
        };

        if (campaignId) {
          const creatives = await creativeManager.getCreativesByCampaignId(
            campaignId,
            false,
          );
          //刪除 frequency 屬性
          const { id, frequency, ...cleanBasic } = camPaign.basic;

          if (camPaign.basic.adType === AdType.SHOP) {
            handleAddCreatives([]);
          } else {
            handleAddCreatives(creatives);
          }
          if (camPaign.basic?.selectPattern === SelectPattern.AUTO) {
            if (creatives.length > 0) {
              setCampaign({
                ...camPaign,
                basic: {
                  ...cleanBasic,
                  name: `${camPaign.basic.name} 副本`,
                  spents: 0,
                  expectedSpent: 0,
                  startDate: copyStartDate(),
                  endDate: copyEndDate(),
                  state: 1,
                },
                autoProducts: updateCampaignProducts(
                  creatives,
                  'product',
                ) as Product[],
              });
              useProductStore.setState({
                selectedAutoProducts: updateCampaignProducts(
                  creatives,
                  'product',
                ) as Product[],
              });
            } else {
              setCampaign({
                ...camPaign,
                basic: {
                  ...cleanBasic,
                  name: `${camPaign.basic.name} 副本`,
                  spents: 0,
                  expectedSpent: 0,
                  startDate: copyStartDate(),
                  endDate: copyEndDate(),
                  state: 1,
                },
              });
            }
          } else {
            if (
              creatives &&
              [
                AdType.KEYWORD,
                AdType.SMART_KEYWORD,
                AdType.MAX_CVR_KEYWORD,
                AdType.RECOMMEND,
                AdType.OUTBOUND_KEYWORD,
              ].includes(camPaign?.basic?.adType as AdType)
            ) {
              setCampaign({
                ...camPaign,
                basic: {
                  ...cleanBasic,
                  name: `${camPaign.basic.name} 副本`,
                  spents: 0,
                  expectedSpent: 0,
                  startDate: copyStartDate(),
                  endDate: copyEndDate(),
                  state: 1,
                },
                products: updateCampaignProducts(
                  creatives,
                  'product',
                ) as Product[],
              });
              useProductStore.setState({
                selectedProducts: updateCampaignProducts(
                  creatives,
                  'product',
                ) as Product[],
              });
            } else {
              setCampaign({
                ...camPaign,
                basic: {
                  ...cleanBasic,
                  name: `${camPaign.basic.name} 副本`,
                  spents: 0,
                  expectedSpent: 0,
                  startDate: copyStartDate(),
                  endDate: copyEndDate(),
                  state: 1,
                },
              });
            }
          }
          // 寫入campaign到store
          handleSetCampaign({
            ...camPaign,
            basic: {
              ...cleanBasic,
              name: `${camPaign.basic.name} 副本`,
              spents: 0,
              expectedSpent: 0,
              startDate: copyStartDate(),
              endDate: copyEndDate(),
              state: 1,
            },
          });
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (params['campaignId']) {
      const fetchCampaign = async (campaignId: string) => {
        try {
          if (campaignId) {
            const campaign =
              await defaultCampaignManager.getCampaign(campaignId);
            useCampaignStore.setState({ adType: campaign.basic.adType });

            // 移除 keywords 內所有物件的 id
            if (Array.isArray(campaign.keywords)) {
              campaign.keywords = campaign.keywords.map((k) => _.omit(k, 'id'));
            }

            if (campaign.basic && !campaign.basic.bidMode) {
              campaign.basic.bidMode = SelectPattern.AUTO;
            }
            fetchCreative(campaignId, campaign);
          }
        } catch (e) {
          console.error(e);
        }
      };
      fetchCampaign(params['campaignId']);
    }
    return () => {
      handleAddCreatives([]);
      resetProductStore();
    };
  }, []);

  if (_.isEmpty(defaultCampaign)) {
    return <LoadingIndicator />;
  }
  return <>{!_.isEmpty(defaultCampaign) && formType()}</>;
};
export default CopyPage;
